"use client"

import * as React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { useTranslations } from "@/components/I18nProvider"
import {
  Button,
  Input,
  Textarea,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Alert,
  AlertDescription,
} from "@sarkiai/ui"

import { toast } from "sonner"
import { Loader, Send, CheckCircle2, User, Mail, Building2 } from "lucide-react"

const demoSchema = z.object({
  fullName: z.string().min(2, "Name is required"),
  email: z.string().email("Invalid email address"),
  company: z.string().min(2, "Company is required"),
  content: z.string().min(10, "Message must be at least 10 characters long"),
  serviceName: z.string(),
})

type DemoFormValues = z.infer<typeof demoSchema>

interface DemoRequestFormProps {
  serviceName: string
}

export function DemoRequestForm({ serviceName }: DemoRequestFormProps) {
  const t = useTranslations("corporate_services.demo_request")
  const errorT = useTranslations("error")

  const [isPending, setIsPending] = React.useState(false)
  const [isSuccess, setIsSuccess] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const form = useForm<DemoFormValues>({
    resolver: zodResolver(demoSchema),
    defaultValues: {
      fullName: "",
      email: "",
      company: "",
      content: "",
      serviceName: serviceName,
    },
  })

  // Update service name if it changes
  React.useEffect(() => {
    form.setValue("serviceName", serviceName)
  }, [serviceName, form])

  const handleSubmit = async (values: DemoFormValues) => {
    setIsPending(true)
    setError(null)
    setIsSuccess(false)

    // Prefix content with service name for the generic contact API
    const submissionData = {
      ...values,
      content: `[DEMO REQUEST: ${values.serviceName}]\n\nCompany: ${values.company}\n\n${values.content}`
    }

    try {
      const data = await fetch(
        process.env.NEXT_PUBLIC_API_V1_CONTACT_SUBMIT || '/api/v1/contact',
        {
          method: 'POST',
          body: JSON.stringify(submissionData),
          headers: { 'Content-Type': 'application/json' }
        }
      )

      const result = await data.json()

      if (result?.success) {
        setIsSuccess(true)
        form.reset({
          fullName: "",
          email: "",
          company: "",
          content: "",
          serviceName: serviceName,
        })
        toast.success(t('success'))
      } else {
        setError(result?.reason || errorT('contact_generic_error'))
      }
    } catch (e) {
      console.error(e)
      setError(errorT('contact_generic_error'))
    } finally {
      setIsPending(false)
    }
  }

  if (isSuccess) {
    return (
      <div className="bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-3xl p-12 text-center space-y-4">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-2xl uppercase tracking-tighter font-black">{t('success')}</h3>
        <Button
          variant="outline"
          onClick={() => setIsSuccess(false)}
          className="rounded-2xl h-10 px-10 bg-black text-white dark:bg-white dark:hover:bg-white dark:text-black hover:bg-black hover:opacity-90 uppercase tracking-widest text-xs"
        >
          Envoyer un autre message
        </Button>
      </div>
    )
  }

  return (
    <div className="bg-background border border-black/10 dark:border-white/10 rounded-3xl p-8 md:p-12 space-y-8 shadow-xl shadow-black/5">
      <div className="space-y-2">
        <h3 className="text-2xl md:text-3xl uppercase tracking-tighter font-black">{t('title')}</h3>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {t('description').replace('{{service}}', serviceName)}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[10px] font-black uppercase tracking-widest opacity-60 ml-1">{t('name')} *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        {...field}
                        className="!bg-background border border-muted-foreground/30 dark:border-white/20 pl-10 h-12 rounded-xl focus:border-black dark:focus:border-white focus:ring-0 focus-visible:ring-0"
                        placeholder="JEAN DUPONT"
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[10px] font-black uppercase tracking-widest opacity-60 ml-1">{t('email')} *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        {...field}
                        type="email"
                        className="!bg-background border border-muted-foreground/30 dark:border-white/20 pl-10 h-12 rounded-xl focus:border-black dark:focus:border-white focus:ring-0 focus-visible:ring-0"
                        placeholder="name@company.com"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[10px] font-black uppercase tracking-widest opacity-60 ml-1">{t('company')} *</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      {...field}
                      className="!bg-background border border-muted-foreground/30 dark:border-white/20 pl-10 h-12 rounded-xl focus:border-black dark:focus:border-white focus:ring-0 focus-visible:ring-0"
                      placeholder="NOM DE VOTRE ENTREPRISE"
                      onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="content"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[10px] font-black uppercase tracking-widest opacity-60 ml-1">{t('message')} *</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    className="!bg-background border border-muted-foreground/30 dark:border-white/20 p-4 min-h-[120px] rounded-2xl focus:border-black dark:focus:border-white focus:ring-0 focus-visible:ring-0 resize-none"
                    placeholder="Quels sont vos objectifs ?"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {error && (
            <Alert variant="destructive" className="bg-destructive/10 border-destructive/20 text-destructive rounded-xl">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            disabled={isPending}
            className="w-full h-14 rounded-xl bg-black dark:bg-white dark:text-black uppercase tracking-widest text-xs font-bold gap-2 group"
          >
            {isPending ? (
              <>
                <Loader className="h-5 w-5 animate-spin" />
                {t('submitting') || "Envoi..."}
              </>
            ) : (
              <>
                {t('submit')}
                <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </>
            )}
          </Button>
        </form>
      </Form>
    </div>
  )
}
